// Document manager component
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import Loading from '../common/Loading';
import toast from 'react-hot-toast';
import { 
  DocumentTextIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const DocumentManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await adminAPI.getDocuments();
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = async (data) => {
    try {
      await adminAPI.createDocument(data);
      toast.success('Document template created successfully');
      setShowCreateForm(false);
      reset();
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to create document template');
    }
  };

  if (loading) {
    return <Loading message="Loading document templates..." />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create and manage document templates for automated generation.
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Template
          </button>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map((doc) => (
          <div key={doc._id} className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <DocumentTextIcon className="h-8 w-8 text-primary-600" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">{doc.title}</h3>
                  <p className="text-sm text-gray-500">{doc.category}</p>
                </div>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Variables: {doc.variables?.length || 0}
              </p>
              <p className="text-sm text-gray-500">
                Created: {new Date(doc.createdAt).toLocaleDateString()}
              </p>
            </div>

            <div className="mt-6 flex space-x-3">
              <button className="flex-1 bg-primary-50 text-primary-700 text-sm font-medium py-2 px-3 rounded-md hover:bg-primary-100">
                Edit
              </button>
              <button className="flex-1 bg-gray-50 text-gray-700 text-sm font-medium py-2 px-3 rounded-md hover:bg-gray-100">
                Generate
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Document Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create Document Template
              </h3>
              <form onSubmit={handleSubmit(handleCreateDocument)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    {...register('title', { required: 'Title is required' })}
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Document title"
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    {...register('category', { required: 'Category is required' })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select category</option>
                    <option value="Invoice">Invoice</option>
                    <option value="Notice">Notice</option>
                    <option value="Report">Report</option>
                    <option value="Agreement">Agreement</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Template HTML</label>
                  <textarea
                    {...register('template', { required: 'Template is required' })}
                    rows={6}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter HTML template with {{variables}}"
                  />
                  {errors.template && <p className="mt-1 text-sm text-red-600">{errors.template.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      reset();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Create Template
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;